/**
 * Tenant-Admin → Zone Admin management + ledger.
 *
 * Single page combining:
 *  - Table of zone admins (balance owed, zones linked, drivers)
 *  - Invite modal (create a new ZONE_ADMIN user)
 *  - Drawer to drill into one zone admin: ledger, payouts, adjustments,
 *    list of linked zones (and the commission config per zone)
 *  - Per-zone commission editor (commission_type + percent + admin_user_id)
 *
 * Rider classification (zone driver assignment) lives on the existing
 * Riders page — see ``AdminRiders.jsx``.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, ArrowLeft, IndianRupee, Settings, MapPin, BadgeIndianRupee, Trash2, Users } from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import AdminSearchBar from "../../components/AdminSearchBar";

const METHODS = ["CASH", "UPI", "BANK", "OTHER"];
const COMMISSION_TYPES = [
    { v: "NONE", l: "No revenue share" },
    { v: "ITEM_VALUE_PERCENT", l: "% of item subtotal" },
    { v: "TENANT_COMMISSION_PERCENT", l: "% of tenant's commission" },
];

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
}

export default function AdminZoneAdmins() {
    const [rows, setRows] = useState([]);
    const [zonesList, setZonesList] = useState([]);
    const [query, setQuery] = useState("");
    const [showInvite, setShowInvite] = useState(false);
    const [active, setActive] = useState(null); // drilled zone admin
    const [busy, setBusy] = useState(false);
    const [scope, setScope] = useState("VIEW");
    const [scopeBusy, setScopeBusy] = useState(false);

    const load = async () => {
        try {
            const [a, b, s] = await Promise.all([
                api.get("/admin/zone-admin-ledger"),
                api.get("/admin/zones"),
                api.get("/admin/tenant-settings"),
            ]);
            setRows(a.data || []);
            setZonesList(b.data || []);
            setScope((s.data?.zone_admin_shop_access || "VIEW").toUpperCase());
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    };
    useEffect(() => { load(); }, []);

    const updateScope = async (v) => {
        setScopeBusy(true);
        try {
            await api.patch("/admin/tenant-settings", { zone_admin_shop_access: v });
            setScope(v);
            toast.success("Zone Admin access updated");
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setScopeBusy(false); }
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => `${r.first_name || ""} ${r.last_name || ""} ${r.mobile_number || ""}`.toLowerCase().includes(q));
    }, [rows, query]);

    const openDrill = async (user_id) => {
        try {
            const { data } = await api.get(`/admin/zone-admin-ledger/${user_id}`);
            setActive(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load ledger"); }
    };

    const submitInvite = async (form) => {
        setBusy(true);
        try {
            await api.post("/admin/zone-admins", form);
            toast.success("Zone admin invited — they'll log in via OTP");
            setShowInvite(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const archive = async (id) => {
        if (!window.confirm("Archive this zone admin? Their zones will be unlinked and their drivers will lose their classification. Past ledger entries are preserved.")) return;
        try {
            await api.delete(`/admin/zone-admins/${id}`);
            toast.success("Archived");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="admin-zone-admins-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Zone Admins</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} zone admins · revenue share + zone-driver payouts</p>
                </div>
                <button data-testid="invite-zone-admin" onClick={() => setShowInvite(true)} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-bold text-sm inline-flex items-center gap-2"><Plus size={14} /> Invite zone admin</button>
            </div>

            <div className="mt-4">
                <AdminSearchBar value={query} onChange={setQuery} placeholder="Search by name or mobile…" testid="za-search" count={filtered.length} />
            </div>

            <div className="mt-4 bg-card border border-border rounded-2xl p-4" data-testid="za-scope-card">
                <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Shop access for Zone Admins</p>
                <p className="text-xs text-muted-foreground mt-0.5 mb-3">Decide whether zone admins can browse and manage the shops in their zone(s) — they always see their earnings &amp; ledger.</p>
                <div className="flex gap-2">
                    {["NONE", "VIEW", "EDIT"].map((v) => (
                        <button
                            key={v}
                            onClick={() => updateScope(v)}
                            disabled={scopeBusy}
                            data-testid={`za-scope-${v}`}
                            className={`px-4 py-2 rounded-xl text-xs font-semibold border transition ${scope === v ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted/40 hover:bg-muted"}`}
                        >
                            {v === "NONE" ? "No shop access" : v === "VIEW" ? "View only" : "Full edit (like merchant)"}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-4 bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                    <thead><tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                        <th className="px-4 py-3">Zone admin</th>
                        <th className="px-4 py-3">Zones</th>
                        <th className="px-4 py-3">Drivers</th>
                        <th className="px-4 py-3">Commission</th>
                        <th className="px-4 py-3">Delivery</th>
                        <th className="px-4 py-3">Paid</th>
                        <th className="px-4 py-3">Balance</th>
                        <th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                        {filtered.map((r) => (
                            <tr key={r.user_id} data-testid={`za-row-${r.user_id}`} className="border-t border-border">
                                <td className="px-4 py-3">
                                    <p className="font-semibold">{r.first_name} {r.last_name || ""}</p>
                                    <p className="text-xs text-muted-foreground">+91 {r.mobile_number}</p>
                                </td>
                                <td className="px-4 py-3 font-bold">{r.zones_linked}</td>
                                <td className="px-4 py-3 font-bold">{r.zone_drivers}</td>
                                <td className="px-4 py-3 text-emerald-700 font-bold">{inr(r.earn_commission)}</td>
                                <td className="px-4 py-3 text-sky-700 font-bold">{inr(r.earn_delivery)}</td>
                                <td className="px-4 py-3 text-muted-foreground">{inr(r.total_paid)}</td>
                                <td className="px-4 py-3 font-black">{inr(r.balance)}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button data-testid={`open-za-${r.user_id}`} onClick={() => openDrill(r.user_id)} className="text-xs text-primary font-bold mr-3">Open ledger →</button>
                                    <button data-testid={`archive-za-${r.user_id}`} onClick={() => archive(r.user_id)} className="text-xs text-destructive"><Trash2 size={12} className="inline" /></button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground"><Users size={28} className="inline opacity-40" /><p className="mt-2">No zone admins yet — invite one to start sharing revenue.</p></td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Zones section — set/unset commission per zone */}
            <h2 className="font-bold mt-8 text-lg">Zones · revenue share</h2>
            <p className="text-xs text-muted-foreground mb-2">Pick a zone admin per zone + the commission model.</p>
            <div className="bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[680px]">
                    <thead><tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                        <th className="px-4 py-3">Zone</th>
                        <th className="px-4 py-3">Zone admin</th>
                        <th className="px-4 py-3">Commission type</th>
                        <th className="px-4 py-3">%</th>
                        <th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                        {zonesList.map((z) => (
                            <ZoneCommissionRow key={z.id} zone={z} admins={rows} onSaved={load} />
                        ))}
                        {zonesList.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No zones defined yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {showInvite && <InviteModal busy={busy} onClose={() => setShowInvite(false)} onSubmit={submitInvite} />}
            {active && <ZoneAdminDrawer data={active} onClose={() => setActive(null)} onAction={() => { openDrill(active.zone_admin.id); load(); }} />}
            <style>{`.inp{display:block;background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%;margin-top:4px}`}</style>
        </div>
    );
}


function ZoneCommissionRow({ zone, admins, onSaved }) {
    const [adminId, setAdminId] = useState(zone.admin_user_id || "");
    const [type, setType] = useState(zone.commission_type || "NONE");
    const [pct, setPct] = useState(zone.commission_percent || 0);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/zones/${zone.id}/commission`, {
                // iter181 — the chosen admin is saved whatever the revenue share: "No revenue share" used to send
                // no admin at all, which left the branch without an admin (no shops, no order notifications).
                admin_user_id: adminId || null,
                commission_type: type,
                commission_percent: Number(pct) || 0,
            });
            toast.success("Saved");
            onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <tr className="border-t border-border" data-testid={`zone-com-row-${zone.id}`}>
            <td className="px-4 py-3">
                <p className="font-semibold inline-flex items-center gap-1"><MapPin size={12} /> {zone.name}</p>
            </td>
            <td className="px-4 py-3">
                <select data-testid={`zone-${zone.id}-admin`} className="bg-muted rounded-lg px-2 py-1 text-xs font-semibold" value={adminId} onChange={(e) => setAdminId(e.target.value)}>
                    <option value="">— none —</option>
                    {admins.map((a) => <option key={a.user_id} value={a.user_id}>{a.first_name} {a.last_name || ""}</option>)}
                </select>
            </td>
            <td className="px-4 py-3">
                <select data-testid={`zone-${zone.id}-ctype`} className="bg-muted rounded-lg px-2 py-1 text-xs font-semibold" value={type} onChange={(e) => setType(e.target.value)}>
                    {COMMISSION_TYPES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
            </td>
            <td className="px-4 py-3">
                <input data-testid={`zone-${zone.id}-pct`} type="number" min={0} step="0.1" className="bg-muted rounded-lg px-2 py-1 text-xs font-semibold w-20" value={pct} onChange={(e) => setPct(e.target.value)} disabled={type === "NONE"} />
            </td>
            <td className="px-4 py-3 text-right">
                <button data-testid={`zone-${zone.id}-save`} disabled={saving} onClick={save} className="text-xs font-bold text-primary disabled:opacity-50">
                    {saving ? "Saving…" : "Save"}
                </button>
            </td>
        </tr>
    );
}


function InviteModal({ onClose, onSubmit, busy }) {
    const [form, setForm] = useState({ mobile_number: "", first_name: "", last_name: "", email: "" });
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
            <div className="bg-background rounded-2xl shadow-xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-bold text-lg mb-3">Invite zone admin</h3>
                <p className="text-xs text-muted-foreground mb-3">They'll log in via OTP on the tenant URL — no separate password needed.</p>
                <div className="grid grid-cols-1 gap-2">
                    <input data-testid="invite-mobile" className="inp" placeholder="Mobile number" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} />
                    <input data-testid="invite-firstname" className="inp" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                    <input data-testid="invite-lastname" className="inp" placeholder="Last name (optional)" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                    <input data-testid="invite-email" className="inp" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <button data-testid="submit-invite" disabled={busy || !form.mobile_number || !form.first_name} onClick={() => onSubmit(form)} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy && <Loader2 size={14} className="animate-spin" />} Invite
                </button>
            </div>
        </div>
    );
}


function ZoneAdminDrawer({ data, onClose, onAction }) {
    const [mode, setMode] = useState(null);
    // iter181 — this account's own shop access (overrides the tenant-wide setting) and its on/off switch.
    const [access, setAccess] = useState((data.zone_admin.zone_admin_shop_access || "DEFAULT").toUpperCase());
    const [acctStatus, setAcctStatus] = useState((data.zone_admin.status || "ACTIVE").toUpperCase());
    const saveAccount = async (patch, apply) => {
        setBusy(true);
        try {
            await api.patch(`/admin/zone-admins/${data.zone_admin.id}`, patch);
            apply();
            toast.success("Saved");
            onAction();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    const [payout, setPayout] = useState({ amount: "", payment_method: "UPI", note: "" });
    const [adj, setAdj] = useState({ amount: "", note: "" });
    const [busy, setBusy] = useState(false);

    const submitPayout = async () => {
        if (!payout.amount || Number(payout.amount) <= 0) return toast.error("Amount must be positive");
        setBusy(true);
        try {
            await api.post(`/admin/zone-admin-ledger/${data.zone_admin.id}/payout`, {
                amount: Number(payout.amount), payment_method: payout.payment_method, note: payout.note,
            });
            toast.success("Payout recorded");
            setMode(null); setPayout({ amount: "", payment_method: "UPI", note: "" });
            onAction();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    const submitAdjustment = async () => {
        if (!adj.amount || Number(adj.amount) === 0) return toast.error("Amount cannot be zero");
        setBusy(true);
        try {
            await api.post(`/admin/zone-admin-ledger/${data.zone_admin.id}/adjustment`, {
                amount: Number(adj.amount), note: adj.note,
            });
            toast.success("Adjustment posted");
            setMode(null); setAdj({ amount: "", note: "" });
            onAction();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div data-testid="za-drawer" className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
            <div className="w-full sm:max-w-lg h-full bg-background overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center gap-3">
                    <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg" data-testid="close-za-drawer"><ArrowLeft size={18} /></button>
                    <div>
                        <p className="font-bold">{data.zone_admin.first_name} {data.zone_admin.last_name || ""}</p>
                        <p className="text-xs text-muted-foreground">+91 {data.zone_admin.mobile_number}</p>
                    </div>
                </div>
                <div className="px-5 py-4">
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl py-3"><p className="text-emerald-700 font-black text-base">{inr(data.summary.earn_commission)}</p><p className="text-emerald-700/70 uppercase tracking-wider">Commission</p></div>
                        <div className="bg-sky-50 border border-sky-100 rounded-xl py-3"><p className="text-sky-700 font-black text-base">{inr(data.summary.earn_delivery)}</p><p className="text-sky-700/70 uppercase tracking-wider">Delivery</p></div>
                        <div className="bg-amber-50 border border-amber-100 rounded-xl py-3"><p className="text-amber-700 font-black text-base">{inr(data.summary.balance)}</p><p className="text-amber-700/70 uppercase tracking-wider">Balance</p></div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2 text-center">
                        Paid {inr(data.summary.total_paid)} · Adjustments {inr(data.summary.total_adjustments)} · {data.summary.deliveries} zone-driver deliveries
                    </p>

                    <div className="mt-4 bg-muted/30 rounded-2xl p-3" data-testid="za-account-access">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">This account</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <label className="text-xs font-semibold">Shop access
                                <select data-testid="za-access-select" className="inp" disabled={busy} value={access}
                                        onChange={(e) => { const v = e.target.value; saveAccount({ zone_admin_shop_access: v }, () => setAccess(v)); }}>
                                    <option value="DEFAULT">Same as all zone admins</option>
                                    <option value="NONE">No shop access</option>
                                    <option value="VIEW">View only</option>
                                    <option value="EDIT">Full edit</option>
                                </select>
                            </label>
                            <label className="text-xs font-semibold">Account
                                <select data-testid="za-status-select" className="inp" disabled={busy} value={acctStatus === "ACTIVE" ? "ACTIVE" : "INACTIVE"}
                                        onChange={(e) => { const v = e.target.value; saveAccount({ status: v }, () => setAcctStatus(v)); }}>
                                    <option value="ACTIVE">Active</option>
                                    <option value="INACTIVE">Deactivated — signed out everywhere</option>
                                </select>
                            </label>
                        </div>
                    </div>

                    {data.zones.length > 0 && (
                        <div className="mt-4 bg-muted/30 rounded-2xl p-3">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Linked zones</p>
                            <div className="flex flex-wrap gap-2">
                                {data.zones.map((z) => (
                                    <span key={z.id} className="text-xs bg-card border border-border rounded-full px-2.5 py-1">
                                        <MapPin size={10} className="inline mr-1" />{z.name} · {z.commission_percent}% {z.commission_type === "ITEM_VALUE_PERCENT" ? "of subtotal" : z.commission_type === "TENANT_COMMISSION_PERCENT" ? "of tenant commission" : ""}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-bold">
                        <button data-testid="za-action-payout" onClick={() => setMode("payout")} className="bg-sky-600 text-white rounded-xl py-2 inline-flex items-center justify-center gap-1"><Plus size={12} /> Payout</button>
                        <button data-testid="za-action-adjustment" onClick={() => setMode("adjustment")} className="bg-amber-500 text-amber-950 rounded-xl py-2 inline-flex items-center justify-center gap-1"><IndianRupee size={12} /> Adjustment</button>
                    </div>

                    {mode === "payout" && (
                        <Section title="Record payout">
                            <div className="grid grid-cols-2 gap-3">
                                <label><span className="text-xs font-semibold text-muted-foreground">Amount (₹)</span>
                                    <input data-testid="za-payout-amount" type="number" min={0} step="0.5" className="inp" value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })} />
                                </label>
                                <label><span className="text-xs font-semibold text-muted-foreground">Method</span>
                                    <select data-testid="za-payout-method" className="inp" value={payout.payment_method} onChange={(e) => setPayout({ ...payout, payment_method: e.target.value })}>
                                        {METHODS.map((m) => <option key={m}>{m}</option>)}
                                    </select>
                                </label>
                                <label className="col-span-2"><span className="text-xs font-semibold text-muted-foreground">Note (optional)</span>
                                    <input data-testid="za-payout-note" className="inp" value={payout.note} onChange={(e) => setPayout({ ...payout, note: e.target.value })} placeholder="e.g. weekly settlement" />
                                </label>
                            </div>
                            <button data-testid="save-za-payout" disabled={busy} onClick={submitPayout} className="mt-3 w-full bg-sky-600 text-white rounded-xl py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save payout</button>
                        </Section>
                    )}

                    {mode === "adjustment" && (
                        <Section title="Record adjustment">
                            <p className="text-xs text-muted-foreground mb-2">Positive credits the zone admin, negative debits.</p>
                            <input data-testid="za-adj-amount" type="number" step="0.5" className="inp" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="e.g. -100 for fuel adjustment" />
                            <input data-testid="za-adj-note" className="inp mt-2" value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} placeholder="Reason" />
                            <button data-testid="save-za-adj" disabled={busy} onClick={submitAdjustment} className="mt-3 w-full bg-amber-500 text-amber-950 rounded-xl py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save adjustment</button>
                        </Section>
                    )}

                    <h3 className="font-bold mt-6 mb-2 text-sm">Ledger ({data.entries.length})</h3>
                    <div className="space-y-2">
                        {data.entries.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No entries yet.</p>}
                        {data.entries.map((e) => {
                            const negative = e.kind === "PAYOUT" || (e.kind === "ADJUSTMENT" && e.amount < 0);
                            const tone = {
                                EARNING_COMMISSION: "bg-emerald-100 text-emerald-700",
                                EARNING_DELIVERY: "bg-sky-100 text-sky-700",
                                PAYOUT: "bg-rose-100 text-rose-700",
                                ADJUSTMENT: "bg-amber-100 text-amber-700",
                            }[e.kind] || "bg-muted text-muted-foreground";
                            return (
                                <div key={e.id} data-testid={`za-row-${e.id}`} className="bg-card border border-border rounded-xl px-3 py-2 flex items-center gap-2 text-sm">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${tone}`}>{e.kind.replace("EARNING_", "")}</span>
                                            {e.order_unique_id && <span className="text-[11px] font-bold text-muted-foreground">#{e.order_unique_id}</span>}
                                            {e.zone_name && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{e.zone_name}</span>}
                                            {e.payment_method && <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{e.payment_method}</span>}
                                        </div>
                                        {e.note && <p className="text-xs mt-0.5 truncate">{e.note}</p>}
                                        <p className="text-[11px] text-muted-foreground">{fmtDate(e.created_at)}</p>
                                    </div>
                                    <p className={`font-black text-sm ${negative ? "text-rose-600" : "text-emerald-700"}`}>{negative ? "−" : "+"}{inr(Math.abs(e.amount))}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}


function Section({ title, children }) {
    return (
        <div className="mt-4 bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold text-sm mb-3">{title}</h3>
            {children}
        </div>
    );
}
